import { ChartData, ChartOptions, TooltipItem } from "chart.js";
import { TFunction } from "i18next";

import { ResolutionType } from "../../../../store/common_types";
import { ConversionSearchResultChartItem, PercentValue } from "../../../../store/conversions/types";
import { resolutionUnitMap } from "../../../../utils/resolution";
import { formatNumber } from "../../../../utils/table";

const datasetsMapColor = [
  { key: "searches", backgroundColor: "rgb(54, 162, 235)", borderColor: "rgba(54, 162, 235)" },
  { key: "errorsResponses", backgroundColor: "rgb(255, 99, 132, .6)", borderColor: "rgb(255, 99, 132)" },
  { key: "timeouts", backgroundColor: "rgb(222, 169, 55, .6)", borderColor: "rgba(222, 169, 55)" },
  { key: "successfulResponses", backgroundColor: "rgb(4, 201, 135, .6)", borderColor: "rgba(4, 201, 135)" },
  { key: "zerosResponses", backgroundColor: "rgb(66, 66, 66, .6)", borderColor: "rgb(66, 66, 66)" },
];

export const handleSummaryResultsChartData = (
  t: TFunction,
  items: ConversionSearchResultChartItem[]
): ChartData<"line", ConversionSearchResultChartItem[]> => ({
  datasets: datasetsMapColor.map(({ key, backgroundColor, borderColor }) => ({
    label: t(`chart.searchResult.${key}`),
    parsing: { yAxisKey: `${key}${key !== "searches" ? ".percent" : ""}`, xAxisKey: "timestamp" },
    fill: key !== "searches",
    data: items,
    cubicInterpolationMode: "monotone",
    tension: 0.4,
    yAxisID: key === "searches" ? "y1" : "y",
    backgroundColor,
    borderColor,
  })),
});

export const handleSummaryResultsChartOption = (
  t: TFunction,
  min: number,
  max: number,
  minRange: number,
  resolution: ResolutionType
): ChartOptions<"line"> => ({
  spanGaps: false,
  responsive: true,
  plugins: {
    datalabels: { display: false },
    legend: { position: "bottom" },
    tooltip: {
      position: "nearest",
      callbacks: {
        label: (tooltipItem: TooltipItem<"line">): string | string[] => {
          const { label } = tooltipItem.dataset;
          const key = (tooltipItem.dataset as any).parsing.yAxisKey.split(
            "."
          )[0] as keyof ConversionSearchResultChartItem;
          const record = (tooltipItem.dataset as any).data[tooltipItem.dataIndex] as ConversionSearchResultChartItem;

          if (typeof record[key] !== "number") {
            return `${label}: ${formatNumber((record[key] as PercentValue).value)} (${tooltipItem.formattedValue} %)`;
          }
          return `${label}: ${tooltipItem.formattedValue}`;
        },
      },
    },
    zoom: { limits: { x: { min, max, minRange } }, zoom: { enabled: minRange < max - min, mode: "x", threshold: 1 } },
  },
  interaction: { intersect: false, mode: "index" },
  elements: { point: { radius: 0 } },
  scales: {
    y: {
      stacked: true,
      beginAtZero: true,
      min: 0,
      max: 100,
      title: { display: true, text: t(`chart.legends.searchResults`) },
    },
    y1: {
      type: "linear",
      display: true,
      position: "right",
      stacked: false,
      grid: { drawOnChartArea: false },
      title: { display: true, text: t(`chart.legends.searches`) },
    },
    x: {
      type: "time",
      time: {
        isoWeekday: 1,
        displayFormats: { minute: "YYYY-MM-DD HH:mm", hour: "YYYY-MM-DD HH:mm", day: "YYYY-MM-DD", month: "YYYY-MM" },
        ...resolutionUnitMap[resolution],
      },
      offset: false,
      ticks: { maxRotation: 0, align: "center" },
      min,
      max,
    },
  },
});
